import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Star as LeadIcon,
  SupportAgent as AssistantIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Groups as GroupsIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";

const LEAD = "tutor";
// The stored role value stays "helper"; it is presented everywhere as "Tutor Assistant".
const ASSISTANT = "helper";

/**
 * Assign a tutor to groups (course plans) from the tutor's own edit screen.
 *
 * The inverse of the Staff tab on the course-plan page — same join table, same server-side
 * invariants (one lead per group, assistants require a lead, a lead cannot be dropped while assistants
 * remain). Those rules are enforced in CoursePlanStaffService; this UI surfaces them ahead of time
 * where it can, and shows the server's message where it cannot.
 */
export default function TutorGroupAssignment({ tutorId, staged, onStagedChange }) {
  // Add Tutor has no user id yet, so choices are held in the parent and POSTed after creation.
  const isStaging = !tutorId;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [memberships, setMemberships] = useState([]);
  const [groups, setGroups] = useState([]);

  const [groupChoice, setGroupChoice] = useState("");
  const [roleChoice, setRoleChoice] = useState(ASSISTANT);
  const [replaceTarget, setReplaceTarget] = useState(null);

  // Students already enrolled in the highlighted group. Selecting a group means taking on its
  // students, so show exactly who that is before the assignment is made.
  const [groupStudents, setGroupStudents] = useState([]);
  const [groupStudentsLoading, setGroupStudentsLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    // Staging mode has no tutor to scope by, so it reads the plain group list.
    const request = tutorId
      ? api.get(`admin/tutors/${tutorId}/groups`)
      : api.get("admin/course-plan-groups");

    request
      .then((res) => {
        const d = res.data?.data ?? {};
        setMemberships(Array.isArray(d.memberships) ? d.memberships : []);
        setGroups(Array.isArray(d.available_groups) ? d.available_groups : []);
      })
      .catch((e) => setError(e?.response?.data?.message || "Could not load groups."))
      .finally(() => setLoading(false));
  }, [tutorId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!groupChoice) {
      setGroupStudents([]);
      return undefined;
    }
    let cancelled = false;
    setGroupStudentsLoading(true);
    api
      .get(`admin/course-plans/${groupChoice}/students`)
      .then((res) => {
        if (!cancelled) setGroupStudents(res.data?.data?.students ?? []);
      })
      .catch(() => {
        if (!cancelled) setGroupStudents([]);
      })
      .finally(() => {
        if (!cancelled) setGroupStudentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupChoice]);

  const assign = async (planId, role, replaceExisting = false) => {
    if (isStaging) {
      const group = groups.find((g) => g.id === planId);
      onStagedChange?.([
        ...(staged || []).filter((x) => x.course_plan_id !== planId),
        { course_plan_id: planId, role, group_name: group?.name, course_name: group?.course_name },
      ]);
      setGroupChoice("");
      setReplaceTarget(null);
      return;
    }
    setSaving(true);
    try {
      // The server fills the group's subjects in from this tutor's course profile and its message
      // names them, so show that rather than a fixed string.
      const res = await api.post(`admin/tutors/${tutorId}/groups`, {
        course_plan_id: planId,
        role,
        replace_existing: replaceExisting,
      });
      toast.success(
        res.data?.message || (role === LEAD ? "Assigned as lead tutor." : "Added as tutor assistant.")
      );
      setGroupChoice("");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.errors?.user_id?.[0] ||
          e?.response?.data?.message ||
          "Could not assign this group."
      );
    } finally {
      setSaving(false);
      setReplaceTarget(null);
    }
  };

  const remove = async (planId, role) => {
    if (isStaging) {
      onStagedChange?.((staged || []).filter((x) => x.course_plan_id !== planId));
      return;
    }
    setSaving(true);
    try {
      await api.delete(`admin/tutors/${tutorId}/groups/${planId}`, { data: { role } });
      toast.success("Removed from group.");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.errors?.user_id?.[0] ||
          e?.response?.data?.message ||
          "Could not remove this group."
      );
    } finally {
      setSaving(false);
    }
  };

  const selected = groups.find((g) => g.id === groupChoice);

  const handleAdd = () => {
    if (!groupChoice) return;
    // Taking over as lead removes the incumbent, so confirm rather than silently replace.
    if (roleChoice === LEAD && selected?.lead_tutor_id && selected.lead_tutor_id !== tutorId) {
      setReplaceTarget(selected);
      return;
    }
    assign(groupChoice, roleChoice);
  };

  if (loading) return <Skeleton variant="rounded" height={220} />;

  const rows = isStaging
    ? (staged || []).map((x) => ({ ...x, end_date: null }))
    : memberships;
  const assignedPlanIds = new Set(rows.map((m) => m.course_plan_id));
  const options = groups.filter((g) => !assignedPlanIds.has(g.id));
  // A group with no lead cannot take an assistant — the server refuses it, so don't offer it.
  const assistantBlocked = roleChoice === ASSISTANT && selected && !selected.lead_tutor_id;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <GroupsIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>
          Groups
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Course plans this tutor runs as lead, or supports as a tutor assistant. Subjects are assigned
        per group on Resource Allocation → Staff, and whoever holds a subject hosts its live classes.
        A tutor with no subjects on a group covers everything if they are its lead, and hosts nothing
        if they are an assistant.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Subjects</TableCell>
              <TableCell>Ends</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Not assigned to any group.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((m) => (
                <TableRow key={`${m.course_plan_id}-${m.role}`}>
                  <TableCell>{m.group_name}</TableCell>
                  <TableCell>{m.course_name || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={m.role === LEAD ? <LeadIcon /> : <AssistantIcon />}
                      label={m.role === LEAD ? "Lead tutor" : "Tutor Assistant"}
                      sx={{
                        bgcolor: m.role === LEAD ? "#2e7d3218" : "#0277bd18",
                        color: m.role === LEAD ? "#2e7d32" : "#0277bd",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {(m.subjects || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {m.role === LEAD ? "All subjects" : "None — cannot host"}
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {m.subjects.map((sub) => (
                          <Chip
                            key={sub.id}
                            size="small"
                            label={sub.name}
                            sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 600 }}
                          />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>{m.end_date || "—"}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        m.role === LEAD
                          ? "Refused while the group still has tutor assistants"
                          : "Remove from group"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={saving}
                          onClick={() => remove(m.course_plan_id, m.role)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "flex-start" }}>
        <FormControl size="small" sx={{ minWidth: 260, flex: 1 }} disabled={saving}>
          <InputLabel>Add to group</InputLabel>
          <Select
            value={groupChoice}
            label="Add to group"
            onChange={(e) => setGroupChoice(e.target.value)}
          >
            {options.length === 0
              ? [
                  <MenuItem key="none" disabled value="">
                    No further groups available
                  </MenuItem>,
                ]
              : // A group belongs to a course, so list it under that course rather than as a
                // flat name: "Kingston" > "Group A" reads the way people talk about it.
                Object.entries(
                  options.reduce((acc, g) => {
                    const course = g.course_name || "Other";
                    (acc[course] = acc[course] || []).push(g);
                    return acc;
                  }, {})
                ).flatMap(([course, list]) => [
                  <ListSubheader key={`h-${course}`} sx={{ fontWeight: 700, lineHeight: "32px" }}>
                    {course}
                  </ListSubheader>,
                  ...list.map((g) => (
                    <MenuItem key={g.id} value={g.id} sx={{ pl: 3 }}>
                      {g.name}
                      {typeof g.student_count === "number" ? ` · ${g.student_count} students` : ""}
                      {!g.is_active ? " (ended)" : ""}
                    </MenuItem>
                  )),
                ])}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }} disabled={saving}>
          <InputLabel>Role</InputLabel>
          <Select value={roleChoice} label="Role" onChange={(e) => setRoleChoice(e.target.value)}>
            <MenuItem value={LEAD}>Lead tutor</MenuItem>
            <MenuItem value={ASSISTANT}>Tutor Assistant</MenuItem>
          </Select>
        </FormControl>

        <Button
          type="button"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!groupChoice || saving || assistantBlocked}
          onClick={handleAdd}
          sx={{ whiteSpace: "nowrap", height: 40 }}
        >
          Add
        </Button>
      </Stack>

      {/* Students that come with the highlighted group. Assigning the group assigns the tutor to
          all of them, so surface the list before the decision rather than after. */}
      {groupChoice && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <PeopleIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={700}>
              Students in {selected?.name}
            </Typography>
            {!groupStudentsLoading && (
              <Chip size="small" label={`${groupStudents.length} selected`} color="primary" />
            )}
          </Stack>

          {groupStudentsLoading ? (
            <Skeleton variant="rounded" height={56} />
          ) : groupStudents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No students are enrolled in this group yet. The tutor can still be assigned — students
              appear here as they enrol.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                All of these students come with the group — the tutor will see them in their panel.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 140, overflowY: "auto" }}>
                {groupStudents.map((st) => (
                  <Chip
                    key={st.id}
                    size="small"
                    label={st.name || st.email}
                    icon={<CheckIcon />}
                    color="primary"
                    variant="filled"
                  />
                ))}
              </Box>
            </>
          )}
        </Paper>
      )}

      {assistantBlocked && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          “{selected?.name}” has no lead tutor yet. Assign a lead first — tutor assistants work under one.
        </Alert>
      )}
      {roleChoice === LEAD && selected?.lead_tutor_id === tutorId && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          This tutor is already the lead of “{selected?.name}”.
        </Alert>
      )}

      <Dialog open={Boolean(replaceTarget)} onClose={() => setReplaceTarget(null)}>
        <DialogTitle>Replace the lead tutor?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{replaceTarget?.lead_tutor}</strong> is currently lead tutor of{" "}
            <strong>{replaceTarget?.name}</strong> and will be removed. Any tutor assistants stay assigned
            and will work under the new lead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setReplaceTarget(null)}>Cancel</Button>
          <Button
            type="button"
            variant="contained"
            color="warning"
            onClick={() => assign(replaceTarget.id, LEAD, true)}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
